/**
 * CSV parsing and validation utilities
 */

import Papa from 'papaparse';
import { COUNTRIES, BOTTLE_TYPES, BOTTLE_STATUSES, BOTTLE_SIZES } from './constants';

/**
 * Parses a CSV file
 * @param {File} file - CSV file to parse
 * @returns {Promise<{data: Array, errors: Array}>} Parsed data and errors
 */
export function parseCSV(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase().replace(/\s+/g, '_'),
      complete: (results) => {
        resolve({
          data: results.data,
          errors: results.errors
        });
      },
      error: (error) => {
        reject(error);
      }
    });
  });
}

/**
 * Validates bottle CSV data
 * @param {Array} rows - Parsed CSV rows
 * @returns {{validRows: Array, errors: Array}} Validated rows and errors
 */
export function validateBottleCSV(rows) {
  const validRows = [];
  const errors = [];

  rows.forEach((row, index) => {
    const rowErrors = [];
    const rowNum = index + 2;

    if (!row.bottle_name?.trim()) {
      rowErrors.push('Bottle name is required');
    }

    if (!row.type?.trim()) {
      rowErrors.push('Type is required');
    } else if (!BOTTLE_TYPES.includes(row.type.trim())) {
      rowErrors.push(`Invalid type: ${row.type}. Must be one of: ${BOTTLE_TYPES.join(', ')}`);
    }

    if (row.status?.trim() && !BOTTLE_STATUSES.includes(row.status.trim())) {
      rowErrors.push(`Invalid status: ${row.status}. Must be one of: ${BOTTLE_STATUSES.join(', ')}`);
    }

    if (row.size?.trim() && !BOTTLE_SIZES.includes(row.size.trim())) {
      rowErrors.push(`Invalid size: ${row.size}. Must be one of: ${BOTTLE_SIZES.join(', ')}`);
    }

    if (row.proof) {
      const proof = parseFloat(row.proof);
      if (isNaN(proof) || proof <= 0 || proof > 200) {
        rowErrors.push('Proof must be a number between 0 and 200');
      }
    }

    if (row.age_months) {
      const age = parseInt(row.age_months, 10);
      if (isNaN(age) || age < 0) {
        rowErrors.push('Age must be a positive number');
      }
    }

    if (row.purchase_price) {
      const price = parseFloat(row.purchase_price);
      if (isNaN(price) || price < 0) {
        rowErrors.push('Purchase price must be a positive number');
      }
    }

    if (row.purchase_date) {
      const date = new Date(row.purchase_date);
      if (isNaN(date.getTime())) {
        rowErrors.push('Invalid purchase date format (use YYYY-MM-DD)');
      }
    }

    if (rowErrors.length > 0) {
      errors.push({ row: rowNum, errors: rowErrors });
    } else {
      validRows.push(normalizeBottleRow(row));
    }
  });

  return { validRows, errors };
}

/**
 * Resolves distillery names to IDs against user's existing distilleries
 * @param {Array} rows - Validated bottle rows
 * @param {Array} distilleries - User's distilleries [{id, distillery_name}]
 * @returns {{validRows: Array, errors: Array}} Rows with distillery_id and any resolution errors
 */
export function resolveDistilleryNames(rows, distilleries) {
  const distilleryMap = new Map(
    distilleries.map(d => [d.distillery_name.toLowerCase().trim(), d.id])
  );

  const validRows = [];
  const errors = [];

  rows.forEach((row, index) => {
    const rowNum = index + 2;
    const distilleryName = row.distillery_name?.trim();

    if (distilleryName) {
      const distilleryId = distilleryMap.get(distilleryName.toLowerCase());
      if (!distilleryId) {
        errors.push({
          row: rowNum,
          errors: [`Distillery "${distilleryName}" not found. Create it first or leave blank.`]
        });
      } else {
        const { distillery_name, ...rest } = row;
        validRows.push({ ...rest, distillery_id: distilleryId });
      }
    } else {
      const { distillery_name, ...rest } = row;
      validRows.push({ ...rest, distillery_id: null });
    }
  });

  return { validRows, errors };
}

/**
 * Validates distillery CSV data
 * @param {Array} rows - Parsed CSV rows
 * @returns {{validRows: Array, errors: Array}} Validated rows and errors
 */
export function validateDistilleryCSV(rows) {
  const validRows = [];
  const errors = [];

  rows.forEach((row, index) => {
    const rowErrors = [];
    const rowNum = index + 2;

    if (!row.distillery_name?.trim()) {
      rowErrors.push('Distillery name is required');
    }

    if (!row.country?.trim()) {
      rowErrors.push('Country is required');
    } else if (!COUNTRIES.includes(row.country.trim())) {
      rowErrors.push(`Invalid country: ${row.country}. Must be one of: ${COUNTRIES.join(', ')}`);
    }

    if (row.year_founded) {
      const year = parseInt(row.year_founded, 10);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year < 1600 || year > currentYear) {
        rowErrors.push(`Year founded must be between 1600 and ${currentYear}`);
      }
    }

    if (rowErrors.length > 0) {
      errors.push({ row: rowNum, errors: rowErrors });
    } else {
      validRows.push(normalizeDistilleryRow(row));
    }
  });

  return { validRows, errors };
}

/**
 * Normalizes a bottle row from CSV
 */
function normalizeBottleRow(row) {
  return {
    bottle_name: row.bottle_name?.trim(),
    distillery_name: row.distillery_name?.trim() || null,
    type: row.type?.trim(),
    status: row.status?.trim() || 'Unopened',
    size: row.size?.trim() || '750ml',
    proof: row.proof ? parseFloat(row.proof) : null,
    age_months: row.age_months ? parseInt(row.age_months, 10) : null,
    purchase_price: row.purchase_price ? parseFloat(row.purchase_price) : null,
    purchase_date: row.purchase_date || null,
    notes: row.notes?.trim() || null,
    tasting_notes: row.tasting_notes?.trim() || null,
    store_pick: row.store_pick?.trim() || null,
    finish_cask: row.finish_cask?.trim() || null,
    single_barrel: parseBoolean(row.single_barrel),
    bottled_in_bond: parseBoolean(row.bottled_in_bond),
    cask_strength: parseBoolean(row.cask_strength),
    small_batch: parseBoolean(row.small_batch),
    double_barreled: parseBoolean(row.double_barreled)
  };
}

/**
 * Normalizes a distillery row from CSV
 */
function normalizeDistilleryRow(row) {
  return {
    distillery_name: row.distillery_name?.trim(),
    country: row.country?.trim(),
    address: row.address?.trim() || null,
    city: row.city?.trim() || null,
    state_region: row.state_region?.trim() || null,
    year_founded: row.year_founded ? parseInt(row.year_founded, 10) : null
  };
}

/**
 * Parses boolean values from CSV
 */
function parseBoolean(value) {
  if (!value) return false;
  const lower = String(value).toLowerCase().trim();
  return lower === 'true' || lower === 'yes' || lower === '1';
}

/**
 * Generates CSV template content for bottles
 * @returns {string} CSV content
 */
export function getBottleTemplate() {
  const headers = [
    'bottle_name', 'distillery_name', 'type', 'status', 'proof', 'age_months', 'purchase_price',
    'purchase_date', 'size', 'single_barrel', 'bottled_in_bond', 'cask_strength',
    'small_batch', 'double_barreled', 'store_pick', 'finish_cask', 'notes', 'tasting_notes'
  ];
  const example = [
    'Buffalo Trace', 'Buffalo Trace Distillery', 'Bourbon', 'Unopened', '90', '48', '29.99',
    '2024-01-15', '750ml', 'false', 'false', 'false',
    'false', 'false', '', '', 'Great everyday bourbon', 'Vanilla, caramel, oak'
  ];
  return `${headers.join(',')}\n${example.join(',')}`;
}

/**
 * Generates CSV template content for distilleries
 * @returns {string} CSV content
 */
export function getDistilleryTemplate() {
  const headers = ['distillery_name', 'country', 'address', 'city', 'state_region', 'year_founded'];
  const example = ['Buffalo Trace Distillery', 'United States', '113 Great Buffalo Trace', 'Frankfort', 'Kentucky', '1773'];
  return `${headers.join(',')}\n${example.join(',')}`;
}

/**
 * Downloads a string as a CSV file
 * @param {string} content - CSV content
 * @param {string} filename - Download filename
 */
export function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}
